import { EngineeringNav } from '@/components/EngineeringNav';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useState } from 'react';

export default function EngineeringAbout() {
  const [mapZoomed, setMapZoomed] = useState(false);

  return (
    <div className="min-h-screen bg-black" data-testid="engineering-about">
      <EngineeringNav />
      
      {/* Hero */}
      <section className="bg-[#0a0a0a] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-nunito text-sm text-[#F54021] mb-4 tracking-wider uppercase font-bold">About Us</p>
            <h1 className="font-nunito text-4xl lg:text-5xl font-extrabold text-white mb-6">
              The Story Behind BJ Engineering
            </h1>
          </div>
        </div>
      </section>

      {/* About Content - Image and text side by side */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Image - smaller */}
            <div className="w-64 h-64 flex-shrink-0 rounded-2xl overflow-hidden border border-[#333333]">
              <img 
                src="https://customer-assets.emergentagent.com/job_cad-craft-studio-1/artifacts/juwe2x2h_ChatGPT%20Image%20Feb%202%2C%202026%2C%2002_44_38%20PM.png" 
                alt="Ben Jefferies - Founder of BJ Engineering" 
                className="w-full h-full object-cover"
              />
            </div>

            {/* Text - beside image */}
            <div className="flex-1">
              <h2 className="font-nunito text-3xl font-bold text-white mb-6">Our Founder</h2>
              <p className="font-nunito text-lg text-gray-300 leading-relaxed">
                BJ Engineering was founded by Ben Jefferies in 2020, with a desire to deliver accessible 
                and cost effective engineering services, locally. Ben had struggled to source manufacturing 
                and reverse engineering services locally, and found timescales to be excessive. So BJE was born!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-nunito text-3xl font-bold text-white mb-12 text-center">Our Location</h2>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Real Map with zoom effect */}
            <div 
              className="rounded-2xl overflow-hidden border border-[#333333] relative cursor-pointer"
              onMouseEnter={() => setMapZoomed(true)}
              onMouseLeave={() => setMapZoomed(false)}
            >
              <div className="relative w-full h-[400px] overflow-hidden">
                {/* Zoomed out view - England & Wales with Monmouth marker */}
                <div 
                  className={`absolute inset-0 transition-opacity duration-500 ${mapZoomed ? 'opacity-0' : 'opacity-100'}`}
                >
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2524088.575268!2d-3.5!3d52.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4871a644c4a1c5ed%3A0xd4e7be0e8fef0ae4!2sMonmouth!5e0!3m2!1sen!2suk!4v1707000000000!5m2!1sen!2suk"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Map of England and Wales"
                  />
                </div>
                
                {/* Zoomed in view - Bristol, Swindon, Hereford area */}
                <div 
                  className={`absolute inset-0 transition-opacity duration-500 ${mapZoomed ? 'opacity-100' : 'opacity-0'}`}
                >
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d315984.575268!2d-2.716!3d51.811!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4871a644c4a1c5ed%3A0xd4e7be0e8fef0ae4!2sMonmouth!5e0!3m2!1sen!2suk!4v1707000000000!5m2!1sen!2suk"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Map showing Monmouth area"
                  />
                </div>
              </div>
              
              {/* Hover hint */}
              <div className={`absolute bottom-4 left-4 bg-black/70 text-white text-xs px-3 py-1 rounded-full transition-opacity ${mapZoomed ? 'opacity-0' : 'opacity-100'}`}>
                Hover to zoom
              </div>
            </div>

            {/* Location Text */}
            <div>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#F54021]/20 border border-[#F54021] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#F54021]" />
                </div>
                <div>
                  <h3 className="font-nunito text-2xl font-bold text-white mb-4">Serving the Local Area & Beyond</h3>
                  <p className="font-nunito text-lg text-gray-300 leading-relaxed">
                    We regularly serve customers in the Monmouthshire, Herefordshire and Gloucestershire area, 
                    but are happy to travel even internationally!
                  </p>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-[#1a1a1a] rounded-2xl border border-[#333333]">
                <h4 className="font-nunito font-bold text-white mb-3">Based in Monmouth</h4>
                <p className="font-nunito text-gray-400">
                  Our workshop is centrally located in Monmouth, providing easy access to customers 
                  throughout South Wales and the West of England.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-nunito text-3xl font-bold text-white mb-4">
            Ready to Work Together?
          </h2>
          <p className="font-nunito text-gray-400 mb-8">
            Get in touch to discuss your project requirements.
          </p>
          <Link 
            to="/engineering/quote"
            className="btn-engineering inline-flex items-center gap-2 px-8 py-4"
            data-testid="get-quote-about-btn"
          >
            Get a Quote
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#333333] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-nunito text-sm text-gray-500">© 2024 BJ Engineering. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
